export const useLicenseStore = defineStore('licenseStore', () => {
    const { session } = useUserSession();
    const { public: publicConfig } = useRuntimeConfig();

    const initialized = ref(false);

    const licensePacks = ref({});
    const productCodesWithLicenses = ref([]);

    const unloading = ref(false);

    // Compose the product store
    const productStore = useProductStore();
    const { currentProductCode, products } = storeToRefs(productStore);

    const unload = () => {
        unloading.value = true;

        // Reset the store
        initialized.value = false;
        licensePacks.value = {};
        productCodesWithLicenses.value = [];

        unloading.value = false;
    };

    // Check if the product licenses are loaded
    const productLicensesLoaded = computed(() => {
        return (
            initialized.value &&
            Object.keys(licensePacks.value ?? {}).length > 0
        );
    });

    // Get the license for the current product
    const currentProductLicenses = computed(() => {
        if (!initialized.value) {
            return {};
        }

        return licensePacks.value?.[currentProductCode.value] ?? {};
    });

    const fetchLicenses = async (withAuth) => {
        const Authorization = withAuth.startsWith('Bearer ')
            ? withAuth
            : `Bearer ${withAuth}`;

        const [_error, response] = await catchError(
            $fetch(`${publicConfig.appHost}/api/v1/licenses`, {
                method: 'GET',
                headers: {
                    Authorization,
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    'X-Context-Platform-Public-Auth-Token':
                        publicConfig.platform.authToken,
                },
            }),
        );

        if (!response.data) {
            return;
        }

        const { data } = response;

        // Set the license packs
        licensePacks.value = data?.license_packs ?? {};
        productCodesWithLicenses.value = Object.keys(licensePacks.value);

        initialized.value = true;
    };

    /**
     * Load the licenses for the current product
     *
     * @param {*} [withAuthHeader=undefined]
     * @param {boolean} [force=false]
     */
    const loadLicenses = async (withAuthHeader = undefined, force = false) => {
        // If the store is initialized and not forced, do not reload
        if (productLicensesLoaded.value && !force) {
            return;
        }

        const authHeader = withAuthHeader
            ? withAuthHeader
            : session.value?.accessToken;

        // If there is no auth header, we cannot fetch the licenses
        if (!authHeader) {
            return;
        }

        await catchError(fetchLicenses(authHeader));
    };

    /**
     * Add count to the active license count
     *
     * @param {number} [count=1]
     * @return {*}
     */
    const addActiveLicense = (count = 1) => {
        if (!currentProductLicenses.value) {
            return;
        }

        currentProductLicenses.value.in_use_licenses_count += count;
    };

    /**
     * Subtract count from the active license count
     *
     * @param {number} [count=1]
     * @return {*}
     */
    const subtractActiveLicense = (count = 1) => {
        if (!currentProductLicenses.value) {
            return;
        }

        const after =
            currentProductLicenses.value.in_use_licenses_count - count;

        if (after < 0) {
            currentProductLicenses.value.in_use_licenses_count = 0;
        }

        currentProductLicenses.value.in_use_licenses_count = after;
    };

    // Get the products with licenses
    const productsWithLicenses = computed(() => {
        if (!initialized.value || !products.value?.length) {
            return [];
        }

        return productCodesWithLicenses.value.reduce((acc, code) => {
            const product = products.value.find(
                (product) => product.foreign_product_code === code,
            );

            // get the product name
            if (product) {
                acc.push({
                    label: product.name,
                    value: code,
                });
            }

            return acc;
        }, []);
    });

    return {
        unload,
        // refs
        productLicensesLoaded,
        currentProductLicenses,
        productsWithLicenses,
        initialized,
        // methods
        loadLicenses,
        addActiveLicense,
        subtractActiveLicense,
    };
});
